import { useState } from "react";
import { Box, Chip, Collapse, useMediaQuery, useTheme } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import CustomTypography from "../../../Components/Typography";
import CustomMenu from "../../../Components/CustomMenu";
import CustomTable from "../../../Components/CustomTable";
import CustomCheckboxLabel from "../../../Components/CustomCheckboxLabel";
import { categoryDiscountChipStyle, productThumbStyle } from "../styles/styles";
import { getCurrencySymbol } from "../../../Utils/CountriesData/getCountriesData";
import { categoryNamespaces } from "../../../Helpers/CategoryNamespaces";

const ProductsInfo = (props) => {
  const {
    productCategories = [],
    selectedCategoryIds,
    onToggleCategory,
    onOpenDetail,
    // Rendered once per catalog vertical (stores and restaurant menus). The two
    // have separate category trees, so the label lookups need to know which.
    vertical = "products",
  } = props;

  const categoryNs = categoryNamespaces(vertical);
  const { t } = useTranslation([
    "supplier-products-info",
    "pricelist-info",
    ...categoryNs,
  ]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [showUnoffered, setShowUnoffered] = useState(false);

  // Mirror the services step: list offered categories, then non-offered ones under a
  // collapsible "Not included in your offer" section (shown, but not selectable).
  const offeredCategories = productCategories.filter((c) => c.is_offered);
  const unofferedCategories = productCategories.filter((c) => !c.is_offered);

  if (!productCategories.length) {
    return (
      <Box>
        <CustomTypography
          props={{
            fontWeight: 500,
            fontSize: "16px",
            color: "#2F2B3D",
            lineHeight: "28px",
          }}
        >
          {t("no_products")}
        </CustomTypography>
      </Box>
    );
  }

  const buildColumns = (category) => {
    const currencySymbol = getCurrencySymbol(
      category.products[0]?.currency || "ILS"
    );

    return [
      {
        id: "product_id",
        label: t("table.product_id"),
        field: "id",
        align: "left",
        headerStyle: { width: { xs: "70px", md: "14%" } },
        cellStyle: { width: { xs: "70px", md: "14%" } },
      },
      {
        id: "product_name",
        label: t("table.product_name"),
        align: "left",
        headerStyle: { width: { xs: "200px", md: "36%" } },
        cellStyle: { width: { xs: "200px", md: "36%" } },
        render: (product) => (
          <Box display="flex" alignItems="center" gap="12px">
            {product.main_image_url ? (
              <img
                src={product.main_image_url}
                alt={product.name}
                style={productThumbStyle}
              />
            ) : (
              <Box sx={{ ...productThumbStyle, backgroundColor: "#F4F4F4" }} />
            )}
            <CustomTypography
              props={{ fontSize: "14px", fontWeight: 400, color: "#3E3F4C" }}
            >
              {product.name}
            </CustomTypography>
          </Box>
        ),
      },
      {
        id: "subcategory",
        label: t("table.subcategory"),
        align: "left",
        headerStyle: { width: { xs: "150px", md: "28%" } },
        cellStyle: { width: { xs: "150px", md: "28%" } },
        render: (product) =>
          product.subcategory_name
            ? t(product.subcategory_name, {
                ns: categoryNs,
                defaultValue: product.subcategory_name,
              })
            : "-",
      },
      {
        id: "price",
        label: t("table.price"),
        align: "left",
        headerStyle: { width: { xs: "90px", md: "16%" } },
        cellStyle: { width: { xs: "90px", md: "16%" } },
        render: (product) => `${currencySymbol}${product.price}`,
      },
      {
        id: "more",
        label: "",
        align: "right",
        headerStyle: { width: "56px" },
        cellStyle: { width: "56px" },
        render: (product) => (
          <CustomMenu
            menuBtnIconPath="/menu_icon.svg"
            menuItems={[
              {
                iconPath: "/file_icon.svg",
                text: t("more_information"),
                onClick: () => onOpenDetail(product),
              },
            ]}
          />
        ),
      },
    ];
  };

  // isOffered=false renders the category as read-only (disabled, indeterminate icon),
  // matching how the services step shows pricelists that aren't in the offer.
  const renderCategory = (category, isOffered = true) => {
    const isSelected = selectedCategoryIds.includes(category.category_id);
    const discount = Math.round(category.discount_rate ?? 0);

    return (
      <Box key={category.category_id}>
        <Box
          display="flex"
          justifyContent="space-between"
          gap="16px"
          mb="12px"
          sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
        >
          <Box
            display="flex"
            alignItems="center"
            sx={{ flexWrap: "wrap", minWidth: 0 }}
          >
            <CustomCheckboxLabel
              checked={isSelected}
              onChange={() => onToggleCategory(category)}
              disabled={!isOffered}
              size="16px"
              dark={true}
              formControlLabelStyle={{
                flexShrink: 0,
                // Matches the services step exactly: override the FormControlLabel's
                // default margin only on mobile, leaving the desktop default intact.
                ...(isMobile && { marginLeft: "-8px" }),
              }}
              icon={
                !isOffered && (
                  <img
                    src="/icons/checked-icons/checkbox-indeterminate-icon.svg"
                    alt="checkbox-indeterminate-icon"
                    width="16px"
                    height="16px"
                  />
                )
              }
              checkedIcon={
                !isOffered && (
                  <img
                    src="/icons/checked-icons/checkbox-indeterminate-icon.svg"
                    alt="checkbox-indeterminate-icon"
                    width="16px"
                    height="16px"
                  />
                )
              }
            />
            <CustomTypography
              props={{
                color: "#2F2B3D",
                fontWeight: 500,
                fontSize: "16px",
                lineHeight: "28px",
              }}
            >
              {t(category.category_name, {
                ns: categoryNs,
                defaultValue: category.category_name,
              })}
            </CustomTypography>
            {isOffered && (
              <Chip
                label={`${t("category_discount")}: ${discount}%`}
                sx={categoryDiscountChipStyle}
              />
            )}
          </Box>

          {category.files?.length > 0 && (
            <CustomMenu
              menuBtnText={t("files_certificates")}
              menuBtnIconPath="/file_icon.svg"
              menuItems={category.files.map((file) => ({
                iconPath: "/file_download_icon.svg",
                text: file.name,
                onClick: () =>
                  window.open(file.url, "_blank", "noopener,noreferrer"),
              }))}
            />
          )}
        </Box>

        {/* Wrap in a card matching the services accordions
            (border 1px #EAEAEA, radius 16px, no shadow). The nested overrides
            flatten CustomTable's sticky first column + per-cell corner radii
            (which mis-render in RTL) so the wrapper owns the rounded corners. */}
        <Box
          className="custom-scrollbar"
          sx={{
            border: "1px solid #EAEAEA",
            borderRadius: "16px",
            backgroundColor: "#fff",
            // Table scrolls horizontally on mobile (full-width card); clipped on desktop.
            overflowY: "hidden",
            overflowX: { xs: "auto", md: "hidden" },
            // CustomTable is built to fill a fixed-height flex parent (height:100%
            // + overflow:auto). Inside this auto-height card that forces a scroll
            // area whose RTL scrollbar shows as a square in the top corner — so
            // collapse it to natural height with no internal scroll.
            "& .custom-table-container": {
              height: "auto !important",
              overflow: "visible !important",
            },
            "& .MuiTableContainer-root": {
              height: "auto !important",
              overflow: "visible !important",
            },
            // global.scss adds left/right borders + corner radii to the first/last
            // cells of `.custom-table-container` (and ::before/::after) — that's the
            // corner "square". Neutralize them; the wrapper owns the border/radius.
            "& .custom-table-container::before, & .custom-table-container::after": {
              display: "none !important",
            },
            "& .MuiTableCell-root": {
              position: "static !important",
              left: "auto !important",
              borderLeft: "none !important",
              borderRight: "none !important",
              borderRadius: "0 !important",
              boxShadow: "none !important",
              backgroundColor: "transparent !important",
            },
            "& .MuiTableCell-head": {
              borderTop: "none !important",
            },
          }}
        >
          <CustomTable
            columns={buildColumns(category)}
            data={category.products}
            stickyHeader={false}
            tableStyle={{
              borderRadius: 0,
              backgroundColor: "transparent",
              // Desktop: fixed layout aligns the two tables' columns.
              // Mobile: size to content (the card scrolls horizontally).
              tableLayout: { xs: "auto", md: "fixed" },
              width: { xs: "max-content", md: "100%" },
              minWidth: "100%",
            }}
            containerStyle={{
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              backgroundColor: "transparent",
              paddingRight: 0,
              paddingBottom: 0,
              width: "100%",
            }}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap="24px">
      {offeredCategories.map((category) => renderCategory(category, true))}

      {unofferedCategories.length > 0 && (
        <Box>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap="8px"
            sx={{ cursor: "pointer", pr: "16px" }}
            onClick={() => setShowUnoffered((prev) => !prev)}
          >
            <CustomTypography
              props={{
                fontWeight: 500,
                fontSize: "16px",
                color: "#2F2B3D",
                lineHeight: "28px",
              }}
            >
              {t("not_in_offer", { ns: "pricelist-info" })}
            </CustomTypography>

            <ExpandMore
              sx={{
                transform: showUnoffered ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s",
              }}
            />
          </Box>

          <Collapse in={showUnoffered} timeout={300}>
            <Box
              mt="12px"
              display="flex"
              flexDirection="column"
              gap="24px"
            >
              {unofferedCategories.map((category) =>
                renderCategory(category, false)
              )}
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

export default ProductsInfo;
